import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ColorModule.scss';
import { WidgetPreview } from './WidgetPreview';
import { useFormik } from 'formik';
import { ModalWindow } from '../../modal/ModalWindow';
import { ImageCropper } from '../../common/ImageCropper';
import { Area } from 'react-easy-crop';
import { getCroppedImg } from '../../../utils/imageCrop';
import { FormInput } from '../../merchants/FormInput';
import {
	FORM_FIELDS, FORM_FIELDS_NAMES, IWidget, convertToInitial, getWidgetSchema,
	initialValues, getFormattedFormFieldsForAdmin, getFormattedFormFieldsForMerchant
} from '../../../constants/widget-configuration/widget-configuration-form';
import { ChangeColorInput } from '../widget-configuration/ChangeColorInput';
import { UploadLogo } from '../widget-configuration/UploadLogo';
import { CornerRadiusComponent } from '../widget-configuration/CornerRadiusComponent';
import { WidgetLink } from '../widget-configuration/WidgetLink';
import { WIDGET_LINK_INPUTS } from '../../../constants/widget-configuration/widget-links-info';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { ButtonComponent } from '../../ButtonComponent';
import { fetchWidgetConfigurationData, setWidgetConfigurations } from '../../../store/merchnts/actions/widget';
import { SectionWrapper } from '../../SectionWrapper';
import { useParams } from 'react-router-dom';
import { Spinner } from '../../Spinner';
import { InputType } from '../../form/form-input-constants';

const fonts = [
	{
		value: 'Noto Sans',
		label: 'Noto Sans'
	},
	{
		value: 'Arial',
		label: 'Arial'
	},
	{
		value: 'Helvetica',
		label: 'Helvetica'
	},
	{
		value: 'Gotham',
		label: 'Gotham'
	},
	{
		value: 'Calibri',
		label: 'Calibri'
	}
];

enum integrationTypes {
	OPT_OUT = 'With opt-out',
	NO_OPT_OUT = 'No opt-out',
	FRICTIONLESS = 'Frictionless'
}

const integrationTypesData = [
		{
			value: 1,
			label: integrationTypes.NO_OPT_OUT,
		},
		{
			value: 2,
			label: integrationTypes.OPT_OUT,
		},
		{
			value: 3,
			label: integrationTypes.FRICTIONLESS,
		},
	];

export interface IRGBColor {
	r: number;
	g: number;
	b: number;
}

interface Props {
	mid?: string;
	isAdmin?: boolean;
}

export const ColorModule = (props: Props): React.ReactElement => {
	const { mid, isAdmin } = props;
	const { t } = useTranslation();

	const { merchantState: { widgetConfigurationData: data, isWidgetConfigurationDataLoading }, dispatch } = useMerchants();
	const formik = useFormik<IWidget>({
		initialValues,
		validationSchema: getWidgetSchema(t),
		onSubmit: () => void 0
	});
	const { id, siteId } = useParams<{ id: string, siteId: string }>();
	const [selectedFile, setSelectedFile] = useState(undefined);
	const [preview, setPreview] = useState<string | undefined>();
	const [pixelCrop, setPixelCrop] = useState<Area | undefined>();
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [uploadedFile, setUploadedFile] = useState<string | undefined>();
	const [buttonColorText, setButtonColorText] = useState<string>('white');
	const [linkColor, setLinkColor] = useState<string>('');
	const [isDisable, setIsDisable] = useState<boolean>(false);

	const colorPicker = (color: IRGBColor): void => {
		const sum = Number(`${color.r}`) + Number(`${color.g}`) + Number(`${color.b}`);
		if (sum > 600) {
			setButtonColorText('#181c32');
			setLinkColor('blue');
		} else {
			setButtonColorText('white');
			setLinkColor(formik.values[FORM_FIELDS.BUTTON_COLOR]);
		}
	};

	const hexToRgb = (hex): IRGBColor => {
		const bigint = parseInt(hex, 16);
		const r = (bigint >> 16) & 255;
		const g = (bigint >> 8) & 255;
		const b = bigint & 255;

		return { r, g, b };
	};

	useEffect(() => {
		if (!selectedFile) {
			setPreview(undefined);
			return;
		}

		const objectUrl = URL.createObjectURL(selectedFile);
		// @ts-ignore
		setPreview(objectUrl);

		return (): void => URL.revokeObjectURL(objectUrl);
	}, [selectedFile]);

	useEffect(() => {
		if (data) {
			const initialValue = convertToInitial(data);
			setUploadedFile(initialValue.logoFile);
			formik.setValues(initialValue);
			const color = initialValue.buttonColor.split('#');
			colorPicker(hexToRgb(color[1]));
			setLinkColor(data.buttonColor);
		}
	}, [data]);

	useEffect(() => {
		if (isAdmin) {
			// formik.resetForm();
			void fetchWidgetConfigurationData(dispatch, siteId, mid);
		}
	}, []);

	const onSave = async (): Promise<void> => {
		formik.handleSubmit();

		if (!formik.isValid) {
			return;
		}

		setLoading(true);

		let data: IWidget | undefined;

		if (isAdmin) {
			data = { ...getFormattedFormFieldsForAdmin(formik.values) };
		} else {
			data = { ...getFormattedFormFieldsForMerchant(formik.values) };
		}

		if (formik.values[FORM_FIELDS.LOGO_FILE]) {
			const imageArray = formik.values[FORM_FIELDS.LOGO_FILE].split(':');
			if (formik.values[FORM_FIELDS.LOGO_FILE] && imageArray[0] !== 'https') {
				data.logoFile = formik.values.logoFile;
			} else {
				data.logoFile = null;
			}
		}

		await setWidgetConfigurations(data?.id, data);

		setLoading(false);
	};

	const onSelectFile = (e): void => {
		if (!e.target.files || e.target.files.length === 0) {
			setSelectedFile(undefined);
			return;
		}

		const files = e.target.files;
		const reader = new FileReader();

		// Set up the onload handler before calling readAsDataURL
		reader.onload = function (): void {
			// Make sure we have the file data before opening the modal
			setPreview(reader.result as string);
			setIsModalOpen(true);
			// Don't set formik value yet - we'll do that after cropping
		};

		reader.onerror = function(): void {
			console.error('Error reading file');
		};

		// Start reading the file
		reader.readAsDataURL(files[0]);
		setSelectedFile(files[0]);
	};

	const onEditFile = (): void => {
		if (preview) {
			setIsModalOpen(true);
		}
	};

	const onDeleteFile = (): void => {
		formik.setFieldValue(FORM_FIELDS.LOGO_FILE, null);
		formik.setFieldValue(FORM_FIELDS.VISIBLE, false);
		setUploadedFile(undefined);
		setPreview(undefined);
	};

	const modalClose = (): void => {
		setIsModalOpen(false);
	};

	const modalCancel = (): void => {
		setIsModalOpen(false);
		// setPreview(undefined);
	};

	const onModalSave = async (): Promise<void> => {
		try {
			// Check if preview and pixelCrop are defined
			if (!preview) {
				console.error('No image to crop');
				return;
			}

			if (!pixelCrop) {
				console.error('No crop area defined');
				return;
			}

			// Now call getCroppedImg with the validated parameters
			const { file, fileURL } = await getCroppedImg({
				imageSrc: preview,
				pixelCrop
			});
			formik.setFieldValue(FORM_FIELDS.LOGO_FILE, file);
			setUploadedFile(fileURL);
			setIsDisable(true);
		} catch (e) {
			console.error(e);
		}
		setIsModalOpen(false);
	};

	const widgetLinks = WIDGET_LINK_INPUTS({ t });

	if (isWidgetConfigurationDataLoading) {
		return (
			<div className="card site-loading-height">
				<div className="card-body">
					<Spinner pageLoader/>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="d-xl-flex">
				<SectionWrapper
					sectionAdditionalClass="w-xl-50"
					title={t('MERCHANT.WIDGET_CONFIGURATION')}
					isCardBody
				>
					<form className="row">
						{
							isAdmin && (
								<>
									<FormInput
										formik={formik}
										key={FORM_FIELDS.FONT_FAMILY}
										fieldName={FORM_FIELDS.FONT_FAMILY}
										label={t(FORM_FIELDS_NAMES.FONT_FAMILY)}
										inputType={InputType.Select}
										data={fonts}
										required
										additionalClasses="col-sm-6 col-md-6"
										searchable={false}
										cleanable={false}
									/>
									{/*Text color*/}
									<ChangeColorInput
										formik={formik}
										fieldName={FORM_FIELDS.FONT_COLOR}
										fieldLabel={t(FORM_FIELDS_NAMES.FONT_COLOR)}
										placeholder={t('COLOR_MODULE.BTN_PLACEHOLDER')}
										isAdmin={isAdmin}
										additionalClassname="mb-5 col-6"
									/>
								</>

							)
						}

						{/*Upload File*/}
						<UploadLogo
							uploadedFile={uploadedFile}
							formik={formik}
							isDisable={isDisable}
							onDeleteFile={onDeleteFile}
							onSelectFile={onSelectFile}
							onEditFile={onEditFile}
							isAdmin={isAdmin}
							label={t(FORM_FIELDS_NAMES.LOGO_FILE)}
							fieldName={FORM_FIELDS.LOGO_FILE}
							additionalClass="col-5"
							imageAdditionalClass="download-file-logo"
							isWidgetPage
						/>

						<div className="col-1"/>
						{/*Button color & Checkbox*/}
						<ChangeColorInput
							formik={formik}
							fieldName={FORM_FIELDS.BUTTON_COLOR}
							fieldLabel={t(FORM_FIELDS_NAMES.BUTTON_COLOR)}
							placeholder={t('COLOR_MODULE.BTN_PLACEHOLDER')}
							colorPicker={colorPicker}
							isAdmin={isAdmin}
							additionalClassname="mb-5 col-6"
							isCheckbox
						/>
						{
							isAdmin && (
								<>
									{/*Modal radius*/}
									<CornerRadiusComponent
										formik={formik}
										fieldName={FORM_FIELDS.MODAL_RADIUS}
										fieldLabel={t(FORM_FIELDS_NAMES.MODAL_RADIUS)}
									/>
									{/*Button radius*/}
									<CornerRadiusComponent
										formik={formik}
										fieldName={FORM_FIELDS.BUTTON_RADIUS}
										fieldLabel={t(FORM_FIELDS_NAMES.BUTTON_RADIUS)}
									/>
								</>
							)
						}

						{
							widgetLinks.map(item => (
								<WidgetLink
									key={item.key}
									formik={formik}
									{...item}
								/>
							))
						}
						{
							isAdmin && (
								<FormInput
									formik={formik}
									key={FORM_FIELDS.INTERACTION_TYPE}
									fieldName={FORM_FIELDS.INTERACTION_TYPE}
									label={t(FORM_FIELDS_NAMES.INTERACTION_TYPE)}
									inputType={InputType.Select}
									data={integrationTypesData}
									required
									additionalClasses="col-sm-6 col-md-6"
									searchable={false}
									cleanable={false}
								/>
							)
						}
					</form>
					<ButtonComponent
						buttonName={t('COLOR_MODULE.SAVE')}
						additionalClass="btn-sm d-block ms-auto w-75px"
						loading={loading}
						onButtonClick={(): Promise<void> => !loading && onSave()}
					/>
				</SectionWrapper>

				<WidgetPreview
					textColor={formik.values.fontColor}
					backGroundColor={formik.values.buttonColor}
					isChecked={formik.values[FORM_FIELDS.VISIBLE] && Boolean(formik.values[FORM_FIELDS.LOGO_FILE])}
					preview={uploadedFile}
					fontFamily={formik.values.fontFamily}
					buttonColorText={buttonColorText}
					linkColor={linkColor}
					modalRadius={formik.values[FORM_FIELDS.MODAL_RADIUS]}
					buttonRadius={formik.values[FORM_FIELDS.BUTTON_RADIUS]}
				/>

			</div>

			<ModalWindow
				isOpen={isModalOpen}
				modalTitle={t('COLOR_MODULE.CROP_TITLE')}
				confirmButtonText={t('COLOR_MODULE.SAVE')}
				onClose={modalClose}
				onCancel={modalCancel}
				onConfirm={onModalSave}
				contentClassName="position-relative"
				content={
					<ImageCropper
						image={preview}
						setPixelCrop={setPixelCrop}
						aspect={34/ 10}
					/>
				}
			/>
		</>
	);
};

